(() => {
  const escapeSelectorValue = (value) =>
    window.CSS && CSS.escape ? CSS.escape(value) : value.replace(/["\\]/g, '\\$&');

  // Approximate Shopify's handleization for the SKU formats used in the RSWW
  // imported OEM catalogs. This lets us identify an exact part-number match
  // from the stable product handle pattern: vendor-sku.
  const handleize = (value = '') =>
    value
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[’']/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

  const stripHtml = (value = '') => {
    const element = document.createElement('div');
    element.innerHTML = value;
    return (element.textContent || element.innerText || '').trim();
  };

  const escapeRegExp = (value = '') =>
    value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const getProductHandle = (product = {}) => {
    if (product.handle) return handleize(product.handle);

    try {
      const url = new URL(product.url || '', window.location.origin);
      const match = url.pathname.match(/\/products\/([^/?#]+)/i);
      return match ? handleize(match[1]) : '';
    } catch (_) {
      const match = (product.url || '').match(/\/products\/([^/?#]+)/i);
      return match ? handleize(match[1]) : '';
    }
  };

  const getPartNumberFromProduct = (product, vendor) => {
    const productHandle = getProductHandle(product);
    const vendorHandle = handleize(vendor);
    const prefix = vendorHandle ? `${vendorHandle}-` : '';

    if (!prefix || !productHandle.startsWith(prefix)) return '';

    // Shopify handles are lowercase; catalog SKUs are conventionally displayed
    // uppercase when they contain letters.
    return productHandle.slice(prefix.length).toUpperCase();
  };

  const findExactPartMatch = (products, query, vendor) => {
    const cleanedQuery = query.trim();
    if (!cleanedQuery) return null;

    const queryHandle = handleize(cleanedQuery);
    const vendorHandle = handleize(vendor);
    const expectedHandle =
      vendorHandle && queryHandle ? `${vendorHandle}-${queryHandle}` : '';

    // The predictive-search JSON response does not reliably expose a product
    // "handle" property, but it always provides the product URL. Parse the
    // handle from /products/{handle} and compare it to the stable imported
    // RSWW handle pattern, e.g. sullair-1003-4617.
    const handleMatch = products.find(
      (product) => expectedHandle && getProductHandle(product) === expectedHandle
    );

    if (handleMatch) return handleMatch;

    // Secondary fallback: if a response ever exposes a body/description,
    // match the SKU at the end of the generated RSWW description.
    const escapedQuery = escapeRegExp(cleanedQuery);
    const skuAtEnd = new RegExp(`,\\s*${escapedQuery}\\s*$`, 'i');

    return (
      products.find((product) => skuAtEnd.test(stripHtml(product.body || ''))) ||
      null
    );
  };

  const initLargeCatalogSearch = (scope = document) => {
    scope.querySelectorAll('[data-large-catalog-search]').forEach((root) => {
      if (root.dataset.largeCatalogSearchInitialized === 'true') return;
      root.dataset.largeCatalogSearchInitialized = 'true';

      const input = root.querySelector('[data-large-catalog-search-input]');
      const results = root.querySelector('[data-large-catalog-search-results]');
      const clearButton = root.querySelector('[data-large-catalog-search-clear]');
      const spinner = root.querySelector('[data-large-catalog-search-spinner]');

      if (!input || !results) return;

      const vendor = (root.dataset.searchVendor || '').trim();
      const minimumCharacters = Math.max(
        1,
        Number.parseInt(root.dataset.searchMinimum || '2', 10) || 2
      );
      const showImages = root.dataset.searchShowImages !== 'false';

      let timer = null;
      let controller = null;
      let requestNumber = 0;
      let keyboardIndex = -1;

      const setExpanded = (expanded) => {
        input.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        results.hidden = !expanded;
      };

      const setLoading = (loading) => {
        if (spinner) spinner.hidden = !loading;
        if (clearButton) clearButton.hidden = loading || input.value.length === 0;
      };

      const closeResults = () => {
        setExpanded(false);
        keyboardIndex = -1;
      };

      const clearResults = () => {
        results.replaceChildren();
        closeResults();
      };

      const addStatus = (message) => {
        const status = document.createElement('p');
        status.className = 'large-catalog-search__status';
        status.textContent = message;
        results.appendChild(status);
      };

      const buildProductResult = (product, isExactPartMatch = false) => {
        const link = document.createElement('a');
        link.className = 'large-catalog-search__result';
        link.href = product.url;
        link.setAttribute('role', 'option');
        link.setAttribute('tabindex', '-1');

        const imageWrap = document.createElement('span');
        imageWrap.className = 'large-catalog-search__result-image';

        const imageUrl =
          product.featured_image?.url ||
          product.image ||
          product.featured_image ||
          '';

        if (showImages && imageUrl) {
          const image = document.createElement('img');
          image.src = imageUrl;
          image.alt = product.featured_image?.alt || '';
          image.loading = 'lazy';
          imageWrap.appendChild(image);
        } else {
          imageWrap.classList.add('large-catalog-search__result-image--empty');
        }

        const copy = document.createElement('span');
        copy.className = 'large-catalog-search__result-copy';

        const titleRow = document.createElement('span');
        titleRow.className = 'large-catalog-search__result-title-row';

        const title = document.createElement('span');
        title.className = 'large-catalog-search__result-title';
        title.textContent = product.title || 'Product';
        titleRow.appendChild(title);

        if (isExactPartMatch) {
          const exactBadge = document.createElement('span');
          exactBadge.className = 'large-catalog-search__exact-badge';
          exactBadge.textContent = 'Exact match';
          titleRow.appendChild(exactBadge);
        }

        const meta = document.createElement('span');
        meta.className = 'large-catalog-search__result-meta';

        const vendorText = document.createElement('span');
        vendorText.className = 'large-catalog-search__result-vendor';
        vendorText.textContent = product.vendor || vendor;
        meta.appendChild(vendorText);

        const partNumber = getPartNumberFromProduct(product, vendor);
        if (partNumber) {
          const partText = document.createElement('span');
          partText.className = 'large-catalog-search__result-part';
          partText.textContent = `Part # ${partNumber}`;
          meta.appendChild(partText);
        }

        copy.append(titleRow, meta);
        link.append(imageWrap, copy);

        return link;
      };

      const renderResults = (products, query, isExactPartMatch = false) => {
        results.replaceChildren();
        keyboardIndex = -1;

        if (!products.length) {
          addStatus(`No ${vendor || 'catalog'} products found for “${query}”.`);
          setExpanded(true);
          return;
        }

        products.forEach((product) => {
          results.appendChild(buildProductResult(product, isExactPartMatch));
        });

        const footer = document.createElement('div');
        footer.className = 'large-catalog-search__footer';

        if (isExactPartMatch) {
          footer.textContent = `Exact part number match: ${query}`;
        } else {
          footer.textContent =
            products.length >= 10
              ? 'Showing the 10 closest matches. Add more of the part number or name to narrow the results.'
              : `${products.length} matching ${products.length === 1 ? 'product' : 'products'}`;
        }

        results.appendChild(footer);
        setExpanded(true);
      };
      const fetchPredictiveProducts = async ({
        query,
        fields,
        signal,
      }) => {
        const params = new URLSearchParams();
        params.set('q', query);
        params.set('resources[type]', 'product');
        params.set('resources[limit]', '10');
        params.set('resources[limit_scope]', 'each');
        params.set('resources[options][unavailable_products]', 'show');
        params.set('resources[options][fields]', fields);

        const shopRoot =
          window.Shopify?.routes?.root ||
          document.documentElement.getAttribute('data-shop-root') ||
          '/';

        const response = await fetch(
          `${shopRoot}search/suggest.json?${params.toString()}`,
          {
            method: 'GET',
            headers: { Accept: 'application/json' },
            signal,
          }
        );

        if (!response.ok) {
          throw new Error(`Predictive search returned ${response.status}`);
        }

        const payload = await response.json();
        return payload?.resources?.results?.products || [];
      };

      const filterToVendor = (products) => {
        if (!vendor) return products;

        const normalizedVendor = vendor.toLowerCase();
        return products.filter(
          (product) =>
            (product.vendor || '').trim().toLowerCase() === normalizedVendor
        );
      };

      const performSearch = async () => {
        const query = input.value.trim();
        clearButton && (clearButton.hidden = query.length === 0);

        if (query.length < minimumCharacters) {
          controller?.abort();
          clearResults();
          return;
        }

        controller?.abort();
        controller = new AbortController();
        const currentRequest = ++requestNumber;

        setLoading(true);

        try {
          /*
            Stage 1: search SKU only using the customer's raw input.

            This prevents title/description/vendor terms from polluting an
            exact part-number lookup. We then compare the returned product URL
            handle to RSWW's stable vendor-SKU handle pattern.

            Example:
              input:  1003-4617
              handle: sullair-1003-4617
          */
          const skuCandidates = filterToVendor(
            await fetchPredictiveProducts({
              query,
              fields: 'variants.sku',
              signal: controller.signal,
            })
          );

          if (currentRequest !== requestNumber) return;

          const exactPartMatch = findExactPartMatch(
            skuCandidates,
            query,
            vendor
          );

          if (exactPartMatch) {
            renderResults([exactPartMatch], query, true);
            return;
          }

          /*
            Stage 2: no exact SKU exists, so fall back to the broader predictive
            experience for partial SKUs and descriptive searches.
          */
          const scopedQuery = vendor ? `${query} ${vendor}` : query;

          const generalProducts = filterToVendor(
            await fetchPredictiveProducts({
              query: scopedQuery,
              fields: 'title,body,variants.sku,vendor',
              signal: controller.signal,
            })
          );

          if (currentRequest !== requestNumber) return;

          renderResults(generalProducts, query, false);
        } catch (error) {
          if (error.name === 'AbortError') return;

          results.replaceChildren();
          addStatus('Search is temporarily unavailable. Please try again.');
          setExpanded(true);
          console.error('[RSWW large catalog search]', error);
        } finally {
          if (currentRequest === requestNumber) setLoading(false);
        }
      };
      const queueSearch = () => {
        window.clearTimeout(timer);
        timer = window.setTimeout(performSearch, 220);
      };

      const getResultLinks = () =>
        Array.from(results.querySelectorAll('.large-catalog-search__result'));

      const updateKeyboardActive = (links) => {
        links.forEach((link, index) => {
          link.classList.toggle('is-keyboard-active', index === keyboardIndex);
        });

        if (keyboardIndex >= 0 && links[keyboardIndex]) {
          links[keyboardIndex].scrollIntoView({ block: 'nearest' });
        }
      };

      input.addEventListener('input', queueSearch);

      input.addEventListener('keydown', (event) => {
        const links = getResultLinks();

        if (event.key === 'Escape') {
          closeResults();
          return;
        }

        if (!links.length || results.hidden) return;

        if (event.key === 'ArrowDown') {
          event.preventDefault();
          keyboardIndex = (keyboardIndex + 1) % links.length;
          updateKeyboardActive(links);
        } else if (event.key === 'ArrowUp') {
          event.preventDefault();
          keyboardIndex = keyboardIndex <= 0 ? links.length - 1 : keyboardIndex - 1;
          updateKeyboardActive(links);
        } else if (event.key === 'Enter' && keyboardIndex >= 0) {
          event.preventDefault();
          links[keyboardIndex].click();
        }
      });

      input.addEventListener('focus', () => {
        if (results.childElementCount > 0) setExpanded(true);
      });

      clearButton?.addEventListener('click', () => {
        controller?.abort();
        input.value = '';
        clearButton.hidden = true;
        clearResults();
        input.focus();
      });

      document.addEventListener('click', (event) => {
        if (!root.contains(event.target)) closeResults();
      });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initLargeCatalogSearch());
  } else {
    initLargeCatalogSearch();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initLargeCatalogSearch(event.target);
  });
})();
